import { TypographyParagraph } from '../typography'
import clsx from 'clsx'
import { useDropzone } from 'react-dropzone-esm'
import { useMemo, useState } from 'react'
import UploadArrow from '../icons/Arrows/UploadArrow'
import { AudioIcon, CloseIcon, FileIcon } from '../icons'
import Button from '../buttons/button'
import { bytesToMB, getPageCount, getURLForFile } from '#app/utils/misc.js'
import Audio from '../content/Audio'
import ReactPlayer from 'react-player'
import Skeleton from 'react-loading-skeleton'
import { useTheme } from '#app/utils/providers/themeProvider.js'

const MediaUpload = ({
	isValid,
	isError,
	isInactive = false,
	icon,
	containerWidth = 'w-full',
	value = undefined,
	label,
	helper,
	onChange = () => {},
	onDelete = () => {},
	currentFile,
	setFile,
	isUploading,
	acceptedFileTypes,
	maxFileSizeMB = 10,
	onDropError,
}: {
	isValid?: boolean
	isError?: boolean
	isInactive?: boolean
	icon?: React.ReactNode
	containerWidth?: string
	value?: string
	label: string
	helper: string
	onChange?: (e: File) => void
	onDelete?: () => void
	currentFile?: File | undefined
	setFile: (file: File | undefined) => void
	isUploading: boolean
	acceptedFileTypes?: Record<string, string[]>
	maxFileSizeMB?: number
	onDropError?: (error: string) => void
}) => {
	const [fileError, setFileError] = useState('')

	const handleOnDelete = () => {
		setFile(undefined)
	}

	// useEffect(() => {
	// 	if (imageToRender === undefined && value !== undefined && value !== '') {
	// 		setImageToRender(value)
	// 	}
	// }, [value, imageToRender])

	const { getRootProps, getInputProps, open } = useDropzone({
		accept: acceptedFileTypes || {
			'image/*': [],
			'video/*': [],
			'audio/*': [],
			'application/pdf': ['.pdf'],
		},
		noClick: true,
		onDrop: acceptedFiles => {
			setFileError('')
			if (acceptedFiles.length) {
				const newFile = acceptedFiles[acceptedFiles.length - 1]
				const fileSize = bytesToMB(newFile.size)
				if (Number(fileSize) > maxFileSizeMB) {
					const errorMessage = `File size is ${fileSize} MB, please upload a file smaller than ${maxFileSizeMB} MB`
					setFileError(errorMessage)
					if (onDropError) {
						onDropError(errorMessage)
					}
				} else {
					setFile(newFile)
				}
				// setImageToRender(getURLForFile(fileToRender))
				// handleOnChange(fileToRender)
			}
		},
		onError: error => {
			console.error('ERROR DROPZONE', error)
			if (onDropError) {
				onDropError('Error uploading file: ' + error.message)
			}
		},
		onDropRejected: rejectedFiles => {
			if (rejectedFiles.length > 0) {
				const errorMessage = rejectedFiles[0].errors[0]?.message || 'Invalid file type'
				setFileError(errorMessage)
				if (onDropError) {
					onDropError(errorMessage)
				}
			}
		}
	})

	if (currentFile) {
		return (
			<div className="border-dashed', flex flex-col items-center rounded-2xl border border-textInactiveLight dark:border-textInactiveDark">
				<div className="relative flex h-full max-h-[169px] w-full">
					<div
						className="absolute right-4 top-4 z-50 cursor-pointer text-red hover:opacity-55"
						onClick={() => handleOnDelete()}
					>
						<CloseIcon />
					</div>
					<FilePreview file={currentFile} />
				</div>
			</div>
		)
	} else {
		return (
			<div
				{...getRootProps({
					className: clsx(
						'rounded-2xl flex flex-col items-center border border-dashed',
						containerWidth,
						isError || fileError !== ''
							? 'border-red'
							: 'border-textInactiveLight dark:border-textInactiveDark',
					),
				})}
			>
				<div className="max-h[191px] m-6 flex h-full w-full flex-col items-center">
					<input {...getInputProps()} />
					{isError || fileError !== '' ? (
						<div className="text-red">
							<UploadArrow />
						</div>
					) : (
						<UploadArrow />
					)}
					<div className="mt-4 flex flex-col items-center">
						<Button size="medium" variant="secondary" onClick={() => open()}>
							Browse Files
						</Button>
						{fileError === '' ? (
							<TypographyParagraph
								size="small"
								additionalClasses={clsx(
									'mt-4 dark:text-light text-dark font-paragraphSmallBold',
									isError || fileError !== '' ? 'text-red' : '',
								)}
							>
								{label}
							</TypographyParagraph>
						) : null}
						<TypographyParagraph
							size="small"
							additionalClasses={clsx(
								isError || fileError !== ''
									? 'text-red dark:text-red'
									: 'text-textSecondaryLight dark:text-textSecondaryDark',
									'mt-4',
							)}
						>
							{fileError !== '' ? fileError : helper}
						</TypographyParagraph>
					</div>
				</div>
			</div>
		)
	}
}

export default MediaUpload

const FilePreview = ({ file }: { file: File }) => {
	const [pageSize, setPageSize] = useState(0)
	const [hasGottenSize, setHasGottenSize] = useState(false)
	const currentFile = useMemo(() => {
		const getPageCountEffect = async (file: File) => {
			const pageCount = await getPageCount(file)
			setHasGottenSize(true)
			setPageSize(pageCount)
		}
		if (file.type === 'application/pdf') {
			if (hasGottenSize == false) {
				//@ts-ignore
				getPageCountEffect(file)
			}
		}
		return file
	}, [file])

	const currentFileURL = useMemo(() => {
		return getURLForFile(currentFile)
	}, [currentFile])

	const [theme] = useTheme()
	if (currentFile.type.includes('image')) {
		return (
			<div className="flex h-auto w-full flex-col items-center">
				<img
					className="h-full w-auto"
					src={currentFileURL}
					alt={currentFile.name}
				/>
			</div>
		)
	} else if (currentFile.type.includes('video')) {
		return (
			<div className="flex h-auto w-full flex-col items-center">
				<ReactPlayer
					url={currentFileURL}
					controls={true}
					loop={true}
					width={'auto'}
					height={'169px'}
					// onError={e => {
					// 	setHasError(true)
					// }}
					fallback={
						<div className="relative left-0 top-0 h-full w-full object-contain">
							<Skeleton
								baseColor={
									theme === 'light' || theme === undefined
										? '#f5f5f5'
										: '#383838'
								}
								highlightColor={
									theme === 'light' || theme === undefined
										? '#f8f8f8'
										: '#212121'
								}
								borderRadius={'0'}
								inline={true}
								height={'100%'}
								className="absolute mt-0 h-full w-full rounded-none pt-0"
								containerClassName="absolute h-full w-full"
							/>
						</div>
					}
				/>
			</div>
		)
	} else if (currentFile.type.includes('audio')) {
		return (
			<div className="m-6 flex h-[169px] w-full flex-col items-center text-textPrimaryLight dark:text-textPrimaryDark">
				<AudioIcon />
				<TypographyParagraph size="medium" additionalClasses="mt-4">
					{currentFile.name}
				</TypographyParagraph>
				<Audio
					className="mt-4 h-12 w-full"
					containerClassName="w-full"
					audioURL={currentFileURL}
				/>
			</div>
		)
	} else {
		return (
			<div className="m-6 flex h-full w-full flex-col items-center text-textPrimaryLight dark:text-textPrimaryDark">
				<FileIcon />
				<TypographyParagraph
					size="medium"
					additionalClasses="mt-4 text-textPrimaryLight dark:text-textPrimaryDark"
				>
					{currentFile.name}
				</TypographyParagraph>
				<TypographyParagraph
					size="medium"
					additionalClasses="mt-0 text-textSecondaryLight dark:text-textSecondaryDark"
				>
					{pageSize} Pages •{' '}
					{bytesToMB(currentFile.size) === '0.0'
						? '< 0.1'
						: bytesToMB(currentFile.size)}{' '}
					MB
				</TypographyParagraph>
			</div>
		)
	}
}
